import { Alert02Icon, RefreshIcon } from '@hugeicons/core-free-icons';
import { useRouter } from '@tanstack/react-router';
import { type ReactNode, useEffect, useState } from 'react';
import { errorCode, errorReason } from '@/components/feedback/api-error';
import { ErrorState } from '@/components/feedback/error-state';
import { Icon } from '@/components/primitives/icon';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { announce } from '@/lib/announce';
import { cn } from '@/lib/utils';

function clockText(ts: number): string {
  const date = new Date(ts);
  const part = (n: number) => String(n).padStart(2, '0');
  return `${part(date.getUTCHours())}:${part(date.getUTCMinutes())}:${part(date.getUTCSeconds())} UTC`;
}

/** The clock, refreshed every `ms`, so staleness stays true without re-rendering the data. */
function useNow(ms: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(timer);
  }, [ms]);
  return now;
}

/**
 * The parts of a TanStack `UseQueryResult` that QueryState reads. Any
 * `useQuery` result fits, whatever its error type.
 */
interface QueryLike<T> {
  data: T | undefined;
  isPending: boolean;
  isError: boolean;
  isFetching: boolean;
  isPlaceholderData?: boolean;
  error: unknown;
  dataUpdatedAt: number;
  refetch: () => Promise<unknown>;
}

export interface QueryStateProps<T> {
  query: QueryLike<T>;
  /** The region's own skeleton preset, at its final size. */
  skeleton: ReactNode;
  children: (data: T) => ReactNode;
  /** Dims the data to 70% once it is older than this (3 × the polling interval). */
  staleAfterMs?: number;
  /** What is loading, for the error copy and screen readers: "transactions". */
  label: string;
  /** Overrides Go back on the full error. Default: browser history back. */
  onBack?: () => void;
  className?: string;
}

/**
 * Every query region renders through this (§0 rule 6, §6.2), in this order:
 *
 * 1. Data exists: render it. A failed refresh adds a warning bar above it
 *    ("Could not refresh. Showing data from 09:50:40. Retry") and never
 *    removes it. Data older than `staleAfterMs` is dimmed to 70%; placeholder
 *    data after a filter change is dimmed to 50% and marked busy.
 * 2. Pending: the skeleton at its final size, with aria-busy on the region.
 * 3. Error and no data: <ErrorState> with Retry and Go back.
 *
 * A bare "Loading…" never replaces a region.
 */
export function QueryState<T>({
  query,
  skeleton,
  children,
  staleAfterMs,
  label,
  onBack,
  className,
}: QueryStateProps<T>) {
  const router = useRouter();
  const goBack = onBack ?? (() => router.history.back());

  if (query.data !== undefined) {
    const content = children(query.data);
    return (
      <div
        data-slot="query-state"
        data-state="data"
        aria-busy={query.isPlaceholderData ? true : undefined}
        className={cn('flex min-w-0 flex-col', className)}
      >
        {query.isError ? (
          <RefreshErrorBar
            label={label}
            error={query.error}
            updatedAt={query.dataUpdatedAt}
            fetching={query.isFetching}
            onRetry={() => query.refetch()}
          />
        ) : null}
        <DataDim
          updatedAt={query.dataUpdatedAt}
          staleAfterMs={staleAfterMs}
          placeholder={query.isPlaceholderData === true}
        >
          {content}
        </DataDim>
      </div>
    );
  }

  if (query.isPending) {
    return (
      <div
        data-slot="query-state"
        data-state="pending"
        aria-busy="true"
        className={cn('flex min-w-0 flex-col', className)}
      >
        <span className="sr-only">Loading {label}</span>
        {skeleton}
      </div>
    );
  }

  return (
    <div
      data-slot="query-state"
      data-state="error"
      className={cn('flex min-w-0 flex-col', className)}
    >
      <ErrorState
        title={`Could not load ${label}.`}
        reason={errorReason(query.error)}
        code={errorCode(query.error)}
        onRetry={() => query.refetch()}
        onBack={goBack}
      />
    </div>
  );
}

interface DataDimProps {
  updatedAt: number;
  staleAfterMs: number | undefined;
  placeholder: boolean;
  children: ReactNode;
}

/** Re-renders on its own clock, so the data underneath never re-renders as time passes. */
function DataDim({
  updatedAt,
  staleAfterMs,
  placeholder,
  children,
}: Readonly<DataDimProps>) {
  const realNow = useNow(5000);
  const stale =
    staleAfterMs !== undefined &&
    updatedAt > 0 &&
    realNow - updatedAt > staleAfterMs;
  return (
    <div
      className={cn(
        'min-w-0 transition-opacity duration-(--dur-fast)',
        placeholder && 'opacity-50',
        !placeholder && stale && 'opacity-70',
      )}
    >
      {children}
    </div>
  );
}

interface RefreshErrorBarProps {
  /** What failed to refresh, for the announcement: "transactions". */
  label: string;
  /** The query's error; its code shows as a mono chip. */
  error: unknown;
  /** The query's `dataUpdatedAt` (real clock): the time of the data still shown. */
  updatedAt: number;
  /** The query's `isFetching`: Retry shows "Retrying…" meanwhile. */
  fetching: boolean;
  onRetry: () => void;
  className?: string;
}

/**
 * "Could not refresh. Showing data from 09:50:40. Retry" (§6.2): the warning
 * bar above data kept after a failed refresh. QueryState shows it itself;
 * regions that handle their own query (DataTable) render it above their data.
 */
function RefreshErrorBar({
  label,
  error,
  updatedAt,
  fetching,
  onRetry,
  className,
}: Readonly<RefreshErrorBarProps>) {
  const from = clockText(updatedAt);
  const code = errorCode(error);

  useEffect(() => {
    announce(`Could not refresh ${label}. Showing data from ${from}.`);
  }, [label, from]);

  return (
    <div
      data-slot="refresh-error"
      className={cn(
        'mb-2 flex min-h-9 flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-warning-border bg-warning-tint px-3 py-1.5 text-sm text-warning-ink',
        className,
      )}
    >
      <Icon icon={Alert02Icon} size={16} />
      <span className="font-medium">Could not refresh.</span>
      <span>Showing data from {from}.</span>
      {code ? (
        <span className="inline-flex h-5 items-center rounded-sm border border-warning-border px-1.5 font-mono text-xs">
          {code}
        </span>
      ) : null}
      <Button
        size="xs"
        variant="outline"
        className="ml-auto"
        onClick={onRetry}
        disabled={fetching}
        aria-busy={fetching || undefined}
      >
        {fetching ? (
          <Spinner className="size-3" />
        ) : (
          <Icon icon={RefreshIcon} size={14} />
        )}
        {fetching ? 'Retrying…' : 'Retry'}
      </Button>
    </div>
  );
}
